import type {
  PortalLibraryClientCategory,
  PortalLibrarySection,
  PortalLibraryTile,
} from '../../types/portalLibrary.js';

// CLIENT PORTAL LIBRARY — the two list shapes (brief §7) and the typographic
// tile (brief §5). PURE, so every grouping and every sentence is asserted
// without jsdom.
//
// ⚠️⚠️ TWO SHAPES, NOT ONE COMPONENT WITH A FLAG. Monthly campaigns is a TIME
// SERIES (newest first, grouped by year); Alternate campaigns and Kyck college
// are EVERGREEN (a flat grid, searchable; college adds category chips). They answer
// different questions —
// "what did we run in March" against "is there a guide on lighting" — and a
// flag-driven component would end up half of each.

// ---------- the time series ----------

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * `2026-10` → `October 2026`. ⚠️ Hand-rolled rather than Intl so a server and a
 * browser in different locales cannot label the same month two ways, and an
 * unparseable value is shown as-is rather than as "Invalid Date".
 */
/**
 * Where a tile goes: the portal's own document page, which re-checks the viewer
 * on every open (Round 3). ⚠️ Never the report's `/r/:token` link — that is what
 * a client who left used to keep.
 */
export function portalLibraryDocumentHref(entryId: string): string {
  return `/portal/library/${encodeURIComponent(entryId)}`;
}

/**
 * What a client reads when they cannot open a document (Round 3). ⚠️ ONE
 * sentence for every refusal — not shared with them, no longer showing, never
 * existed — because the server deliberately does not say which, and the page
 * must not guess. It points at their account manager, because a client who has
 * been cut off cannot fix it themselves and should not be told to retry.
 */
export const LIBRARY_DOCUMENT_COPY = {
  notAvailableTitle: 'This document isn’t available',
  notAvailableBody:
    'It may have been taken out of the portal, or it isn’t shared with your account. If you think you should be able to see it, contact your Kyck account manager.',
  errorTitle: 'We couldn’t open this document',
  errorBody: 'Something went wrong on our side. Try again in a moment.',
} as const;

export function periodLabel(period: string | null): string {
  if (!period) return 'Undated';
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined;
  return m && month ? `${month} ${m[1]}` : period;
}

export type YearGroup = { year: string; tiles: PortalLibraryTile[] };

/**
 * Newest first, grouped by year. ⚠️ A tile with no period (only possible for a
 * legacy or hand-edited doc — the schema requires one for this section) is kept
 * in a final "Undated" group rather than dropped: a tile that silently vanishes
 * from a client's list is worse than one in an odd place.
 */
export function groupMonthly(tiles: readonly PortalLibraryTile[]): YearGroup[] {
  const sorted = [...tiles].sort((a, b) => {
    if (a.period === b.period) return a.title.localeCompare(b.title);
    if (a.period === null) return 1;
    if (b.period === null) return -1;
    return b.period.localeCompare(a.period); // yyyy-mm sorts as a string
  });
  const groups: YearGroup[] = [];
  for (const t of sorted) {
    const year = t.period ? t.period.slice(0, 4) : 'Undated';
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.tiles.push(t);
    else groups.push({ year, tiles: [t] });
  }
  return groups;
}

// ---------- the evergreen lists ----------
//
// ⚠️⚠️ ROUND 4 §6 — THE CHIPS ARE CATEGORIES; TAGS ARE SEARCH-ONLY. Tags were
// doing two jobs, the chip row and the search terms, and the chip row is the
// FIRST thing a client reads, so it cannot be built from free text. Kyck college
// gets a CONTROLLED, ORDERED list of categories (managed on Tools → Portal
// library, one per item); Alternate campaigns has none and is search plus one
// flat grid. ⚠️ Tags are still searched, and are NEVER rendered to a client.
//
// Kept from the tag-chip design, because it was right: a chip is a FILTER over
// one flat grid, never a heading, so filtering only ever REMOVES tiles and no
// tile can be shown twice; and it is SINGLE-SELECT ("All" plus one chip).

/** Case-insensitive match on title, summary or any tag. Blank matches all. */
export function matchesSearch(tile: PortalLibraryTile, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [tile.title, tile.summary ?? '', ...tile.tags].some((s) => s.toLowerCase().includes(q));
}

/**
 * The chips: the categories the SERVER sent, in their configured order — it
 * already dropped any that none of this viewer's tiles use. ⚠️ Taken from all
 * the tiles, never the filtered ones: a chip that vanished when you searched
 * would take away the control you would use to widen the search again.
 */
export function libraryCategories(
  categories: readonly PortalLibraryClientCategory[],
  tiles: readonly PortalLibraryTile[],
): PortalLibraryClientCategory[] {
  const used = new Set(tiles.map((t) => t.categoryId));
  return categories.filter((c) => used.has(c.id));
}

/**
 * The selected chip, or null for "All". ⚠️ A selection naming a category the
 * section no longer shows (the list refetched) resolves to "All" rather than
 * filtering to nothing — an empty grid under a chip that is no longer on screen
 * has no visible cause and no visible way out.
 */
export function effectiveCategory(
  selected: string | null,
  categories: readonly PortalLibraryClientCategory[],
): string | null {
  return selected !== null && categories.some((c) => c.id === selected) ? selected : null;
}

/**
 * The flat grid: search, then the chip, alphabetical. A FILTER, NEVER A
 * GROUPING — the result is a subset of the input, so a tile appears at most once.
 */
export function filterLibrary(
  tiles: readonly PortalLibraryTile[],
  query: string,
  categoryId: string | null,
): PortalLibraryTile[] {
  return tiles
    .filter((t) => matchesSearch(t, query) && (categoryId === null || t.categoryId === categoryId))
    .sort((a, b) => a.title.localeCompare(b.title));
}

// ---------- copy — every sentence here, so each is asserted ----------

/**
 * ⚠️⚠️ EMPTY STATES ARE CONTENT, NOT A FALLBACK (brief §7). A franchise client
 * with no alternate campaigns needs a sentence saying what the section is and
 * why it is empty, not a blank page that reads as broken.
 */
export const LIBRARY_SECTION_COPY: Record<
  PortalLibrarySection,
  { title: string; intro: string; empty: string }
> = {
  'strategy.monthly': {
    title: 'Monthly campaigns',
    intro: 'The strategy deck for each month’s campaign, newest first.',
    empty:
      'No monthly campaign decks have been shared with you yet. When your account manager publishes the plan for a month, it appears here.',
  },
  // ⚠️ Round 6 §2 (Scott: "Don't say these are optional and replace your
  // monthly"). Framed by WHAT THE SECTION IS, never relative to the monthly plan
  // — "in place of the month's default" and "optional extras outside the monthly
  // plan" both did that. ⚠️ The architect's draft wording, not Scott's own words:
  // he asked for the sense, not the sentence, and may edit it.
  'strategy.alternate': {
    title: 'Alternate campaigns',
    intro:
      'Strategy decks for specific campaigns — seasonal pushes, a particular member type, or a feature or service you want to lead with.',
    empty:
      'There’s nothing here for your account yet. Your account manager adds decks here as they’re built for you.',
  },
  // ⚠️ Round 6 §3: the ONE strategy section for every client who is not a fitness
  // franchise. Self-standing copy — it replaces nothing from this viewer's point
  // of view, because they never had Monthly or Alternate.
  'strategy.campaigns': {
    title: 'Campaigns & promotions',
    intro:
      'Strategy decks for your campaigns and promotions — seasonal pushes, a particular model or service, or an offer you want to lead with.',
    empty:
      'There’s nothing here for your account yet. Your account manager adds decks here as they’re built for you.',
  },
  college: {
    title: 'Kyck college',
    intro: 'Guides and how-tos from the Kyck team, to help you get more from your marketing.',
    empty:
      'Nothing has been shared with you in Kyck college yet. Guides and how-tos from the team appear here as they are published.',
  },
  // ⚠️ Round 6 §4: the nav only offers this section to a viewer with at least one
  // entry, so the empty sentence is reached only by a URL or a tile that has just
  // come down — and it must not read as "where are my custom reports?".
  'reports.custom': {
    title: 'Custom reports',
    intro: 'Reports the Kyck team has built specifically for you.',
    empty: 'There are no custom reports for your account right now.',
  },
};

/**
 * What a filter that found nothing says — distinct from an empty section. It
 * names the chip when one is on, because "nothing matches 'ring'" is untrue as a
 * statement about the section when the reader is only looking at Audio.
 */
export function noMatchesSentence(query: string, categoryName: string | null = null): string {
  const q = query.trim();
  if (categoryName !== null && q) {
    return `Nothing in ${categoryName} matches “${q}”. Try a different word, or choose All.`;
  }
  if (categoryName !== null) return `Nothing is in ${categoryName} yet. Choose All to see everything.`;
  return `Nothing here matches “${q}”. Try a different word, or clear the search.`;
}

// ---------- the typographic tile (brief §5) ----------

/**
 * The tile colours, per section. ⚠️⚠️ KYCK'S OWN PALETTE, NEVER THE CLIENT'S
 * (rule 12): a generated tile never pulls a brand logo, colour or asset in — no
 * inference, no substitution. FIXED palette tokens rather than role tokens,
 * because the tile is a mode-invariant surface. ⚠️ NO VOLT: it is the report's
 * single emphasis colour, and never a background.
 *
 * ⚠️⚠️ COLLEGE WAS INK, WHICH IS THE PAGE (Round 4 §5): `--rpt-surface` is ink in
 * dark mode, so the tile had no colour at all there. It is now MARINE, chosen
 * over kelly-deep because kelly-deep is the same hue family as Alternate's kelly.
 * MEASURED (tileContrast.test.ts computes these from the hex values in
 * index.css, not from this comment):
 *   - title, cream on marine, 3.95:1 — the title is 20px at weight 800, WCAG
 *     LARGE text, threshold 3:1. (Rejected earlier against 4.5:1, which is the
 *     body-text threshold and the wrong one for this title.)
 *   - ⚠️ but the KICKER is 12px regular — body text, 4.5:1 — and cream on marine
 *     FAILS it. So college's kicker sits on a CREAM PLATE in INK, 15.35:1.
 *     `kickerPlate` is null where the tile's own pair already clears 4.5:1.
 *   - the tile against the page: 3.89:1 on dark (ink) and 3.95:1 on light
 *     (cream), both over the 3:1 non-text threshold.
 * ⚠️ Marine means "Google" on the report charts; a tile carries no channel
 * encoding, so there is no ambiguity inside a frame.
 */
export const LIBRARY_TILE_STYLE: Record<
  PortalLibrarySection,
  {
    background: string;
    foreground: string;
    kickerPlate: { background: string; foreground: string } | null;
  }
> = {
  'strategy.monthly': {
    background: 'var(--rpt-petrol)',
    foreground: 'var(--rpt-cream)',
    kickerPlate: null,
  },
  'strategy.alternate': {
    background: 'var(--rpt-kelly)',
    foreground: 'var(--rpt-ink)',
    kickerPlate: null,
  },
  college: {
    background: 'var(--rpt-marine)',
    foreground: 'var(--rpt-cream)',
    kickerPlate: { background: 'var(--rpt-cream)', foreground: 'var(--rpt-ink)' },
  },
  // ⚠️ Round 6: petrol, the Monthly pair. A tile is only ever rendered on its OWN
  // section's list page, so two sections sharing a pair never meet in one frame
  // — and a campaigns viewer never has Monthly at all. tileContrast.test.ts
  // measures every non-college pair against 4.5:1.
  'strategy.campaigns': {
    background: 'var(--rpt-petrol)',
    foreground: 'var(--rpt-cream)',
    kickerPlate: null,
  },
  'reports.custom': {
    background: 'var(--rpt-petrol)',
    foreground: 'var(--rpt-cream)',
    kickerPlate: null,
  },
};
